import React, { useEffect, useState } from 'react';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from "react-i18next";
import PropTypes from 'prop-types';
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import AppInput from "../../../../components/Input/AppInput";
import AppConfig, { BILLING_TYPE, MASK } from "../../../../config/AppConfig";
import AppButton from "../../../../components/CustomButtons/AppButton";
import AppColor from "../../../../utilities/AppColor";
import {
    reduceCreateParticipant,
    reduceUpdateParticipant,
    fetchParticipantDetail,
    reduceChangeStatus
} from "../../../../store/slices/coordinatorParticipant";
import { YupValidations } from 'utils/validate';
import InputAutocompleteAddress from 'components/Input/InputAutocompleteAddress';
import SelectField from 'components/Select/SelectField';

const { APPROVAL_STATUS } = AppConfig;
const maskNumber = '999999999999999';
const maskNumber10 = '9999999999';

const useStyles = makeStyles(() => ({
    textarea: {
        height: '100px !important',
    }
}))

const ParticipantForm = props => {
    const {
        onCancel,
        participantId: partId,
        isAdmin
    } = props;
    const { t } = useTranslation();
    const classes = useStyles();
    const history = useHistory();
    const { id } = useParams();
    const dispatch = useDispatch();
    const [submitting, setSubmitting] = useState(false);
    const [approvalStatus, setApprovalStatus] = useState();
    const [latLng, setLatLng] = useState();
    const [initializeData, setInitializeData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        address1: '',
        address2: '',
        postalCode: '',
        ndisPlanNumber: '',
        phone: '',
        planManagerEmail: '',
        planManagerOrganizationName: '',
        planManagerPhone: '',
        billToAddress: '',
        billingDetail: '',
    });

    const {
        lat,
        lng
    } = latLng || {};

    const isAdd = !id;

    const _submit = (data) => {
        let newData = {
            ...data,
            lat,
            lng
        };
        if (data.billingDetail !== BILLING_TYPE.PLAN_MANAGER.id) {
            delete newData.planManagerEmail;
            delete newData.planManagerOrganizationName;
            delete newData.planManagerPhone;
        }
        setSubmitting(true);
        if (isAdd) {
            dispatch(reduceCreateParticipant(newData))
                .then(() => {
                    setSubmitting(false);
                    history.goBack();
                })
        } else {
            dispatch(reduceUpdateParticipant({ ...newData, id: partId || id }))
                .then(() => {
                    setSubmitting(false);
                    _loadData(partId || id);
                })
        }
    }

    const _loadData = (participantId) => {
        dispatch(fetchParticipantDetail({ id: participantId }))
            .then(({ payload }) => {
                if (payload) {
                    const data = { ...initializeData };
                    setApprovalStatus(payload.approvalStatus);
                    Object.keys(initializeData)
                        .forEach(key => {
                            data[key] = payload[key] || "";
                        })
                    setInitializeData(data);
                }
            })
    }

    useEffect(() => {
        if (!isAdd && !!partId && isAdmin) {
            _loadData(partId);
        }
    }, [partId]);

    useEffect(() => {
        if (!isAdd && !!id && !isAdmin) {
            _loadData(id);
        }
    }, [id]);

    const handleReject = async () => {
        setSubmitting(true);
        await dispatch(reduceChangeStatus({
            id: partId,
            body: {
                approvalStatus: APPROVAL_STATUS.REJECT
            }
        }));
        setSubmitting(false);
        _loadData(partId);
    };

    const handleActive = async () => {
        setSubmitting(true);
        await dispatch(reduceChangeStatus({
            id: partId,
            body: {
                approvalStatus: APPROVAL_STATUS.ACTIVE
            }
        }));
        setSubmitting(false);
        _loadData(partId);
    };

    return (
        <Formik
            initialValues={initializeData}
            validationSchema={Yup.object().shape({
                firstName: YupValidations.requiredString.max(64, "validation.max_length_64"),
                lastName: YupValidations.requiredString.max(64, "validation.max_length_64"),
                email: YupValidations.email,
                address1: YupValidations.requiredString,
                address2: Yup.string().max(240, "validation.max_length_240"),
                postalCode: YupValidations.postalCode,
                ndisPlanNumber: YupValidations.requiredString.max(64, "validation.max_length_64"),
                phone: YupValidations.phone,
                planManagerEmail: Yup.string().when('billingDetail', {
                    is: BILLING_TYPE.PLAN_MANAGER.id,
                    then: YupValidations.email,
                }),
                planManagerOrganizationName: Yup.string().when('billingDetail', {
                    is: BILLING_TYPE.PLAN_MANAGER.id,
                    then: YupValidations.requiredString
                }),
                planManagerPhone: Yup.string().when('billingDetail', {
                    is: BILLING_TYPE.PLAN_MANAGER.id,
                    then: YupValidations.phone
                }),
                billToAddress: YupValidations.requiredString,
                billingDetail: YupValidations.requiredString
            })}
            onSubmit={_submit}
            enableReinitialize
        >
            {({ values, handleSubmit, setFieldValue }) => (
                <>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Field
                                component={AppInput}
                                name="email"
                                label={t('label.email')}
                                required
                            />
                            <Field
                                component={AppInput}
                                name="firstName"
                                label={t('label.first_name')}
                                required
                            />
                            <Field
                                component={AppInput}
                                name="lastName"
                                label={t('label.last_name')}
                                required
                            />
                            <Field
                                component={InputAutocompleteAddress}
                                name="address1"
                                label={t('label.address1')}
                                type="textarea"
                                inputClass={classes.textarea}
                                onChangePostalCode={postalcode => setFieldValue("postalCode", postalcode)}
                                onChangeLatLng={latlng => setLatLng(latlng)}
                                required
                            />
                            <Field
                                component={AppInput}
                                name="address2"
                                label={t('label.address2')}
                                type="textarea"
                                inputClass={classes.textarea}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Field
                                component={AppInput}
                                name="postalCode"
                                label={t('label.postal_code')}
                                mask={maskNumber10}
                                maskChar=""
                                type="tel"
                                required
                            />
                            <Field
                                component={AppInput}
                                name="phone"
                                label={t('label.phone')}
                                mask={MASK.PHONE}
                                maskChar=""
                                type="tel"
                                required
                            />
                            <Field
                                component={AppInput}
                                name="ndisPlanNumber"
                                label={t('label.ndis_plan_number')}
                                required
                            />
                            <Field
                                component={SelectField}
                                name="billingDetail"
                                label={t('label.billing_details')}
                                placeholder={` -- ${t('label.select_fund')} -- `}
                                type="select"
                                options={Object.keys(BILLING_TYPE).map(key => {
                                    return {
                                        value: BILLING_TYPE[key].id,
                                        label: t(BILLING_TYPE[key].label)
                                    }
                                })}
                                required
                            >
                            </Field>
                            <Field
                                component={AppInput}
                                name="billToAddress"
                                label={t('label.billing_to_address')}
                                required
                            />
                            {values.billingDetail === BILLING_TYPE.PLAN_MANAGER.id && (
                                <>
                                    <Field
                                        component={AppInput}
                                        name="planManagerOrganizationName"
                                        label={t('label.organization_name')}
                                        required
                                    />
                                    <Field
                                        component={AppInput}
                                        name="planManagerPhone"
                                        label={t('label.phone')}
                                        required
                                        type="tel"
                                        mask={maskNumber}
                                        maskChar=""
                                    />
                                    <Field
                                        component={AppInput}
                                        name="planManagerEmail"
                                        label={t('label.email')}
                                        required
                                    />
                                </>
                            )}
                        </Grid>
                    </Grid>
                    <Box display="flex" justifyContent="flex-end" alignItems="center" mt={3}>
                        {
                            isAdmin
                                ? (
                                    <React.Fragment>
                                        <AppButton
                                            label={t('button.close')}
                                            onClick={() => onCancel()}
                                            mode="outlined"
                                            buttonColor={AppColor.black}
                                            style={{
                                                marginRight: '1rem'
                                            }}
                                        />
                                        {
                                            approvalStatus !== APPROVAL_STATUS.REJECT &&
                                            approvalStatus !== APPROVAL_STATUS.ACTIVE && (
                                                <AppButton
                                                    label={t('button.reject')}
                                                    onClick={handleReject}
                                                    buttonColor={AppColor.red}
                                                    style={{
                                                        marginRight: '1rem'
                                                    }}
                                                    isLoading={submitting}
                                                    disabled={submitting}
                                                />
                                            )
                                        }
                                        {
                                            approvalStatus !== APPROVAL_STATUS.ACTIVE && (
                                                <AppButton
                                                    label={t('button.active')}
                                                    onClick={handleActive}
                                                    buttonColor={AppColor.green}
                                                    style={{
                                                        marginRight: '1rem'
                                                    }}
                                                    isLoading={submitting}
                                                    disabled={submitting}
                                                />
                                            )
                                        }
                                    </React.Fragment>
                                )
                                : (
                                    <AppButton
                                        label={t('button.back')}
                                        onClick={() => history.goBack()}
                                        mode="outlined"
                                        style={{
                                            marginRight: '1rem'
                                        }}
                                    />
                                )
                        }
                        <AppButton
                            label={t('button.save')}
                            onClick={handleSubmit}
                            isLoading={submitting}
                            disabled={submitting}
                        />
                    </Box>
                </>
            )}
        </Formik>
    )
}

ParticipantForm.propTypes = {
    isAdmin: PropTypes.bool,
    participantId: PropTypes.any,
    onCancel: PropTypes.func
}

export default ParticipantForm;
